import React, { useState, useEffect } from 'react';
import { ChartBarIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import apiClient from '../../utils/api';

interface WeightTrend {
  tag: string;
  category: string;
  breed?: string;
  first_weight_date: string;
  first_weight_kg: number;
  latest_weight_date: string;
  latest_weight_kg: number;
  weight_gain_kg: number;
  days_between: number;
  average_daily_gain_kg: number;
  weight_records_count: number;
}

interface GrowthRateByCategory {
  category: string;
  average_daily_gain_kg: number;
  average_weight_kg: number;
  goat_count: number;
}

interface GrowthRateByBreed {
  breed: string;
  average_daily_gain_kg: number;
  average_weight_kg: number;
  goat_count: number;
}

interface WeightDistribution {
  weight_range: string;
  count: number;
  percentage: number;
}

interface GoatGrowthReport {
  location_id: string;
  location_name: string;
  report_date: string;
  period_start?: string;
  period_end?: string;
  total_goats_tracked: number;
  weight_trends: WeightTrend[];
  growth_by_category: GrowthRateByCategory[];
  growth_by_breed: GrowthRateByBreed[];
  weight_distribution: WeightDistribution[];
  market_ready_count: number;
}

const GoatGrowthReportPage: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [report, setReport] = useState<GoatGrowthReport | null>(null);

  useEffect(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const januaryFirst = new Date(`${currentYear}-01-01T00:00:00+05:30`);
    
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    setEndDate(formatDate(today));
    setStartDate(formatDate(januaryFirst));
  }, []);

  useEffect(() => {
    if (selectedLocation?.id && startDate && endDate) {
      fetchReport();
    }
  }, [selectedLocation?.id, startDate, endDate]);

  const fetchReport = async () => {
    if (!selectedLocation || !startDate || !endDate) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get('/goat/report/growth', {
        params: {
          location_id: selectedLocation.id,
          start: startDate,
          end: endDate,
        },
      });
      setReport(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load growth report');
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return <div className="container-mobile py-6 text-center">Loading...</div>;
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      <div className="text-center">
        <ChartBarIcon className="h-16 w-16 text-teal-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Growth & Weight Tracking Report</h1>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {report && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="text-2xl font-bold text-gray-900">{report.total_goats_tracked}</div>
              <div className="text-sm text-gray-600">Goats Tracked</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="text-2xl font-bold text-green-600">{report.market_ready_count}</div>
              <div className="text-sm text-gray-600">Market Ready (25kg+)</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="text-2xl font-bold text-blue-600">
                {report.growth_by_category.length > 0 
                  ? report.growth_by_category[0].average_daily_gain_kg.toFixed(4)
                  : 'N/A'} kg/day
              </div>
              <div className="text-sm text-gray-600">Avg Daily Gain</div>
            </div>
          </div>

          {report.growth_by_category.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Growth by Category</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Daily Gain</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Weight</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Goats</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {report.growth_by_category.map((cat, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 capitalize">{cat.category}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{cat.average_daily_gain_kg.toFixed(4)} kg/day</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{cat.average_weight_kg.toFixed(2)} kg</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{cat.goat_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {report.weight_distribution.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Weight Distribution</h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {report.weight_distribution.map((dist, idx) => (
                  <div key={idx} className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-lg font-bold text-gray-900">{dist.count}</div>
                    <div className="text-sm text-gray-600">{dist.weight_range}</div>
                    <div className="text-xs text-gray-500">{dist.percentage.toFixed(1)}%</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GoatGrowthReportPage;

